import {
    type FC,
    type ReactNode,
    useCallback,
    useEffect,
    useMemo,
} from "react";
import {
    Button,
    ButtonColour,
    ButtonStyle,
    Sidebar,
    UIFloatingPortal,
} from "@onshopfront/components";
import { useShopfrontDispatch, useShopfrontSelector } from "#/_Bootstrap/hooks.ts";
import { LAYOUT_NOTIFICATIONS_TOGGLE } from "#/Actions/General/LayoutActions.ts";
import {
    deleteAllNotifications,
    fetchNotifications,
    fetchNotificationsIfRequired,
    markAllNotificationsAsRead,
} from "#/Actions/Notifications/NotificationActions.ts";
import { fireEventType } from "#/Utilities/Generic/ActionsHelper.ts";
import { NOTIFICATION_SOUND } from "#/Utilities/Platform/Audio/Audio.ts";
import { playShopfrontSound } from "#/Utilities/Platform/Audio/AudioManager.ts";
import FontAwesomeLight from "../../../Utilities/Platform/FontAwesome/FontAwesomeLight";
import { Notification } from "./Notification";

export const Notifications: FC = () => {
    const dispatch = useShopfrontDispatch();

    const loading = useShopfrontSelector(state => state.notifications.loading);
    const notifications = useShopfrontSelector(state => state.notifications.notifications);
    const open = useShopfrontSelector(state => state.layout.notifications.open);
    const user = useShopfrontSelector(state => state.user?.id);
    const register = useShopfrontSelector(state => state.location.register?.id ?? null);
    const outlet = useShopfrontSelector(state => state.location.outlet?.id ?? null);
    const global = useShopfrontSelector(state => state.location.isGlobal);

    useEffect(() => {
        dispatch(fetchNotificationsIfRequired());
    }, [ dispatch ]);

    const hasUnread = useMemo(() => {
        for(const notification of notifications) {
            if(notification.readAt === null) {
                return true;
            }
        }

        return false;
    }, [ notifications ]);

    // Play the notification sound if new notification
    useEffect(() => {
        if(hasUnread) {
            playShopfrontSound(NOTIFICATION_SOUND);
        }
    }, [ hasUnread ]);

    let content: ReactNode;

    if(!notifications.length) {
        content = (
            <div className="flex flex-col items-center p-8">
                <FontAwesomeLight
                    icon="envelope-open"
                    className="text-2xl text-neutral-500"
                />
                <p className="text-neutral-600 dark:text-neutral-400">
                    There are no notifications.
                </p>
            </div>
        );
    } else {
        content = (
            <div
                className="p-4 space-y-1"
                data-testid="notification-list"
            >
                {notifications.map(notification => {
                    if(notification.deleted) {
                        return null;
                    }

                    return (
                        <Notification
                            key={notification.id}
                            notification={notification}
                        />
                    );
                })}
            </div>
        );
    }

    const onClose = useCallback(() => {
        dispatch(fireEventType(LAYOUT_NOTIFICATIONS_TOGGLE));
    }, [ dispatch ]);

    return (
        <UIFloatingPortal>
            <Sidebar
                open={open}
                onClose={onClose}
                side="right"
                className="z-1 bg-white dark:bg-dark-900"
                wrapperClassName="flex flex-col md:w-[500px] w-screen max-w-full h-full"
                top={50}
            >
                <div className="flex justify-between items-center px-4 pb-4 pt-8">
                    <div className="text-xl pl-4">
                        Notifications
                        <Button
                            style={ButtonStyle.Text}
                            colour={ButtonColour.Neutral}
                            className="text-base ml-2"
                            padding="p-2"
                            onClick={() => {
                                dispatch(fetchNotifications());
                            }}
                        >
                            <FontAwesomeLight icon="sync" spin={loading} />
                        </Button>
                    </div>
                    <Button
                        style={ButtonStyle.Text}
                        colour={ButtonColour.Neutral}
                        className="text-xs"
                        disabled={!user || (!outlet && !global)}
                        data-testid="notifications-bulk-action"
                        onClick={() => {
                            if(!user) {
                                return;
                            }

                            if(!outlet && !global) {
                                return;
                            }

                            if(register && !outlet) {
                                return;
                            }

                            if(hasUnread) {
                                dispatch(markAllNotificationsAsRead(
                                    outlet,
                                    register
                                ));
                            } else {
                                dispatch(deleteAllNotifications(
                                    outlet,
                                    register
                                ));
                            }
                        }}
                    >
                        {hasUnread ? "Mark All as Read" : "Delete All"}
                    </Button>
                </div>
                {content}
            </Sidebar>
        </UIFloatingPortal>
    );
};
